import { actionSheetController, alertController, SearchbarCustomEvent, toastController } from '@ionic/core';
import { Component, forceUpdate, Fragment, h, Listen, Prop, State } from '@stencil/core';
import { AlbumDoc, FeedItemSubType, FeedItemType, FileDoc, FileType, PhotoAnalysis, PhotoDoc } from 'common';
import { ChangeEvent } from 'src/global/ConcourseDb';
import { User } from 'src/global/models';
import { PermissionsSet } from 'src/global/models/User';
import { FeedService } from 'src/global/service/FeedService';
import { FileService } from 'src/global/service/FileService';
import { ImageService } from 'src/global/service/ImageService';
import { PhotosSelectorModal } from './photos-selector-modal';

enum Tabs {
  TIMELINE = 'timeline',
  ALBUMS = 'albums',
  RECYCLE_BIN = 'bin',
}

@Component({
  tag: 'concourse-photos',
  styleUrls: ['../../global/app.css', 'concourse-photos.css'],
  shadow: true,
})
export class ConcoursePhotos {
  static FILE_TYPES: string[] = [FileType.PHOTO, FileType.ALBUM];
  @Prop() projectId: string;

  @State() progress: number = 0;
  @State() files: FileDoc[] = [];
  @State() tab: string = Tabs.TIMELINE;

  @State() selected: PhotoDoc[] = [];
  @State() searchTerm: string = '';
  @State() toast: HTMLIonToastElement;

  private analysisQueue: PhotoDoc[] = [];
  private filePermissions: PermissionsSet;

  get user(): User {
    return globalThis.user;
  }

  get isMobile(): boolean {
    return globalThis.isMobile;
  }

  get canWrite() {
    return this.user.canDoBasicThings(this.projectId);
  }

  get pageTitle(): string {
    if (this.tab == Tabs.ALBUMS) {
      if (this.album) return this.album.name || 'Album';
      return 'Albums';
    }
    if (this.tab == Tabs.RECYCLE_BIN) return 'Trash';
    return 'Photos';
  }

  get album(): AlbumDoc {
    const parts = window.location.hash.split('/');
    const albumId = parts[parts.indexOf('albums') + 1];
    return this.files.find(f => f._id == albumId) as AlbumDoc;
  }

  async saveFile(file: FileDoc): Promise<FileDoc> {
    const saved = await FileService.saveFile(this.projectId, file);
    this.files = [...this.files.filter(f => f._id != saved._id), saved];
    return saved;
  }

  async saveFiles(files: FileDoc[]): Promise<FileDoc[]> {
    const saved = await FileService.saveFiles(this.projectId, files);
    this.files = [...this.files.filter(f => !saved.find(s => s._id == f._id)), ...saved];
    return saved;
  }

  exitAlbum() {
    this.navigateToTab(Tabs.ALBUMS);
  }

  async componentWillLoad() {
    this.filePermissions = this.user.getRfiPermissions(this.projectId);
    this.progress = 0;

    const db = FileService.getFilesDb(this.projectId);
    db.on('progress', (percent: number) => this.showProgress(percent));
    db.on('complete', (docs: FileDoc[]) => {
      this.parseDocs(docs);
      // Empty DB never emits progress to 1; without this, `files.length === 0 && progress < 1` stays on the spinner forever.
      this.progress = 1;
    });
    db.on('change', async (e: ChangeEvent) => {
      this.parseDocs(await db.getDocs());
    });
    db.on('error', () => {
      this.progress = 1;
    });
    await db.syncRemote();
    this.parseDocs(await db.getDocs());
    if (!db.awaitingInitialSync) {
      this.progress = 1;
    }

    // here we try and fix where the analysis failed last time.
    const photos = this.files.filter(f => f.type == FileType.PHOTO && !f.deleted_by) as PhotoDoc[];
    this.analysisQueue = photos.filter(f => !f.description);
    //this.analyseNextPhoto();

    this.onHashChange();
  }

  showProgress(percent: number) {
    console.log(`percent: ${percent}`);
    this.progress = percent;
  }

  parseDocs(docs: FileDoc[]) {
    this.files = docs;
  }

  @Listen('hashchange', { target: 'window' })
  async onHashChange() {
    console.log('onHashChange: ', window.location.hash);
    const parts = window.location.hash.split('/');
    this.tab = parts[parts.indexOf('photos') + 1] || Tabs.TIMELINE;
    forceUpdate(this); // for navigating into album
  }

  onSearchInput(term: string) {
    this.searchTerm = term.trim().toLowerCase();
  }

  navigateToTab(tab: string) {
    console.log('onSegmentClick: ', tab);
    window.location.hash = `/project/${this.projectId}/photos/${tab}`;
  }

  async toggleSelected(photos: PhotoDoc[]) {
    if (this.toast) {
      this.toast.dismiss();
      this.toast = null;
      this.selected = [];
    }

    if (this.selected.length + photos.length >= 20) {
      const alert = await alertController.create({
        header: 'Max Selection Reached',
        message: 'You can only select up to 20 photos at a time.',
        buttons: ['OK'],
      });
      return await alert.present();
    }

    photos.forEach(photo => {
      if (this.selected.indexOf(photo) == -1) {
        this.selected = [...this.selected, photo];
      } else {
        this.selected = this.selected.filter(p => p._id != photo._id);
      }
    });

    console.log(`concourse-photos selected` + this.selected.length);
  }

  clearSelection() {
    this.selected = [];
    //this.showCheckboxes = false;
  }

  async deleteSelectedPhotos() {
    console.log('deleteSelectedPhotos: ', this.selected);
    const updated = await this.saveFiles(this.selected.map(p => ({ ...p, deleted_by: this.user.id })));
    this.toast = await toastController.create({
      message: `${updated.length} photos have been moved to the trash`,
      duration: 5000,
      position: 'bottom',
      buttons: [
        {
          text: 'Undo',
          handler: () => {
            this.saveFiles(updated.map(p => ({ ...p, deleted_by: null })));
          },
        },
      ],
    });
    await this.toast.present();
    this.selected = [];
    await this.toast.onDidDismiss();
    if (this.toast) {
      this.toast = null;
    }
  }

  async deleteAlbum() {
    const alert = await alertController.create({
      header: 'Confirm',
      message: `Are you sure you want to delete ${this.album.name}?. The photos in this album will not be deleted, but the album itself will be removed.`,
      cssClass: 'modal-content',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Yes',
          handler: () => {
            this.saveFile({ ...this.album, deleted_by: this.user.id });
            window.location.hash = `/project/${this.projectId}/photos/${Tabs.ALBUMS}`;
          },
        },
      ],
    });
    await alert.present();
  }

  async onCreateAlbumClick() {
    const photos = this.selected;
    const alert = await alertController.create({
      header: `New Album`,
      message: `What name would you like to give the new Album?`,
      inputs: [
        {
          name: 'albumName',
          type: 'text',
          placeholder: `Enter album name`,
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Create',
          handler: async data => {
            console.log('Album name entered:', data.albumName);
            const album = { ...FileService.newFile(this.user.id, null, FileType.ALBUM, data.albumName), photos: photos.map(f => f._id) } as AlbumDoc;
            const saved = await this.saveFile(album);
            window.location.hash = `/project/${this.projectId}/photos/albums/${saved._id}`;
            this.selected = [];
          },
        },
      ],
    });
    await alert.present();
  }

  async addPhotosToAlbum() {
    const photos: PhotoDoc[] = await PhotosSelectorModal.present(this.projectId);
    this.assignPhotosToAlbum(photos, this.album);
  }

  async onRemovePhotosClick() {
    const photos = this.selected;
    const photosBefore = [...this.album.photos];
    const photosAfter = photosBefore.filter(photoId => !photos.find(p => p._id == photoId));
    const saved = await this.saveFile({ ...this.album, photos: photosAfter } as AlbumDoc);
    this.toast = await toastController.create({
      message: `${photos.length} photos have been removed from the album`,
      duration: 8000, // auto-dismiss after 2s
      position: 'bottom', // top | middle | bottom
      buttons: [
        {
          text: 'Undo',
          role: 'cancel',
          handler: () => {
            this.saveFile({ ...saved, photos: photosBefore } as AlbumDoc);
          },
        },
        {
          text: 'Dismiss',
        },
      ],
    });
    await this.toast.present();
    this.selected = [];
    await this.toast.onDidDismiss();
    if (this.toast) {
      this.toast = null;
    }
  }

  async onUploaded(photos: PhotoDoc[]) {
    console.log(`concourse-photos onPhotosUploaded :${photos.length}`);

    if (this.album) {
      this.saveFile({ ...this.album, photos: [...this.album.photos, ...photos.map(p => p._id)] } as AlbumDoc);
    }
    this.saveFiles(photos);

    this.analysisQueue.push(...photos);
    this.analyseNextPhoto();
    photos.forEach(async photo => {
      const feedItem = FeedService.newFeedItem(this.user.id, FeedItemType.PROJECT, this.projectId, FeedItemSubType.PHOTO, photo._id);
      await FeedService.storeFeedItem(this.projectId, feedItem);
    });
  }

  async analyseNextPhoto() {
    if (this.analysisQueue.length == 0) return;
    console.log('analyseNextPhoto: ', this.analysisQueue.length);
    const photo = this.analysisQueue[0];
    this.analysisQueue = this.analysisQueue.slice(1);
    const analysis: PhotoAnalysis = await ImageService.analyzePhoto(this.projectId, photo.s3_key);
    const updated = { ...photo, description: analysis.description, tags: analysis.tags };

    this.saveFile(updated);
    if (this.analysisQueue.length > 0) return this.analyseNextPhoto();
  }

  async editSelectedAlbum() {
    console.log('editSelectedAlbum: ');
    const alert = await alertController.create({
      header: `Album Name`,
      message: `Update the name of the album`,
      inputs: [
        {
          name: 'albumName',
          type: 'text',
          value: this.album.name,
          placeholder: `Enter album name`,
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Update',
          handler: async data => {
            console.log('Album name entered:', data.albumName);
            const saved = await this.saveFile({ ...this.album, name: data.albumName });
          },
        },
      ],
    });
    await alert.present();
  }

  async assignSelectedPhotosToAlbum() {
    const albums = this.files.filter(f => f.type === FileType.ALBUM && !f.deleted_by) as AlbumDoc[];
    const actionSheet = await actionSheetController.create({
      header: 'Select Album',
      buttons: albums.map(album => ({
        text: album.name || 'Album',
        data: album._id,
        handler: () => {
          this.assignPhotosToAlbum(this.selected, album);
          this.selected = [];
        },
      })),
      cssClass: 'album-selector',
    });
    await actionSheet.present();
  }

  async assignPhotosToAlbum(photos: FileDoc[], album: AlbumDoc) {
    const originalPhotoIds: string[] = [...album.photos];
    const addedPhotoIds: string[] = photos.filter(p => !originalPhotoIds.find(s => s == p._id)).map(p => p._id);
    console.log([...originalPhotoIds, ...addedPhotoIds]);
    const updatedAlbum = await this.saveFile({ ...album, photos: [...originalPhotoIds, ...addedPhotoIds] } as AlbumDoc);

    this.toast = await toastController.create({
      message: `${addedPhotoIds.length} photos have been added to ${album.name}`,
      duration: 5000,
      position: 'bottom',
      buttons: [
        {
          text: 'Undo',
          handler: () => {
            this.saveFile({ ...updatedAlbum, photos: originalPhotoIds } as AlbumDoc);
          },
        },
      ],
    });
    await this.toast.present();
    await this.toast.onDidDismiss();
    if (this.toast) {
      this.toast = null;
    }
  }

  async restorePhotos(photos: FileDoc[]) {
    console.log('restorePhotos: ', photos);
    this.saveFiles(photos.map(p => ({ ...p, deleted_by: null })));
  }

  async downloadSelectedPhotos() {
    console.log('downloadSelectedPhotos: ', this.selected);
    try {
      await FileService.downloadFilesAsZip(this.projectId, this.selected);
    } catch (error) {
      const toast = await toastController.create({
        message: `Failed to download files: ${error.message}`,
        duration: 3000,
        position: 'bottom',
      });
      await toast.present();
    }
  }

  render() {
    const photosAndAlbums = this.files.filter(f => (f.type == FileType.PHOTO || f.type == FileType.ALBUM) && !f.deleted_by);
    const onlyPhotos = this.files.filter(f => f.type == FileType.PHOTO && !f.deleted_by) as PhotoDoc[];
    const deleted = this.files.filter(f => f.type == FileType.PHOTO && !!f.deleted_by) as PhotoDoc[];
    return (
      <Fragment>
        <concourse-header
          showLogo={true}
          showHamburger={true}
          projectId={this.projectId}
          pageTitle={this.pageTitle}
          shareRoles={this.album ? [this.filePermissions.readRole] : null}
          showBack={!!this.album}
          onBack={() => this.exitAlbum()}
          showSearchField={true}
          searchValue={this.searchTerm}
          onSearchInput={e => this.onSearchInput(e.detail)}
        >
          <ion-buttons>
            {this.album
              ? this.canWrite && [
                <ion-button fill="clear" onClick={() => this.addPhotosToAlbum()}>
                  <ion-icon slot="icon-only" name="add-circle-outline"></ion-icon>
                </ion-button>,
                <ion-button fill="clear" onClick={() => this.editSelectedAlbum()}>
                  <ion-icon slot="icon-only" name="create-outline"></ion-icon>
                </ion-button>,
              ]
              : [
                <ion-button onClick={e => this.navigateToTab(Tabs.TIMELINE)}>
                  <ion-icon slot="icon-only" name="time-outline" class={this.tab == Tabs.TIMELINE ? 'header-icon-selected' : 'header-icon'}></ion-icon>
                </ion-button>,
                <ion-button onClick={e => this.navigateToTab(Tabs.ALBUMS)}>
                  <ion-icon slot="icon-only" src="/assets/icon/photo-album-icon.svg" class={this.tab == Tabs.ALBUMS ? 'header-icon-selected' : 'header-icon'}></ion-icon>
                </ion-button>,
                <ion-button onClick={e => this.navigateToTab(Tabs.RECYCLE_BIN)}>
                  <ion-icon slot="icon-only" src="/assets/icon/trash-open.svg" class={this.tab == Tabs.RECYCLE_BIN ? 'header-icon-selected' : 'header-icon'}></ion-icon>
                </ion-button>,
              ]}
          </ion-buttons>
        </concourse-header>
        {this.files.length === 0 && this.progress < 1 ? (
          <div class="center-content" style={{ height: '100%' }}>
            <ion-toolbar style={{ width: '180px' }}>
              <circular-progress-bar slot="start" value={this.progress} style={{ paddingRight: '12px' }} />
              <ion-label slot="secondary">loading photos</ion-label>
            </ion-toolbar>
          </div>
        ) : (
          <div class="scroll-container">
            {this.tab == Tabs.TIMELINE && (
              <photos-timeline
                projectId={this.projectId}
                photos={onlyPhotos}
                searchTerm={this.searchTerm}
                selected={this.selected}
                showCheckboxes={!this.isMobile || this.selected.length > 0}
                showUploadOptions={this.canWrite}
                onToggleSelected={e => this.toggleSelected(e.detail)}
                onUploaded={e => this.onUploaded(e.detail)}
              ></photos-timeline>
            )}
            {this.tab == Tabs.ALBUMS && (
              <photos-albums
                projectId={this.projectId}
                files={photosAndAlbums}
                searchTerm={this.searchTerm}
                selected={this.selected}
                showCheckboxes={!this.isMobile || this.selected.length > 0} /* checkboxes always show on desktop */
                showCreateAlbum={this.canWrite}
                onToggleSelected={e => this.toggleSelected([e.detail])}
                onUploaded={e => this.onUploaded(e.detail)}
                onCreateAlbum={() => this.onCreateAlbumClick()}
              ></photos-albums>
            )}
            {this.tab == Tabs.RECYCLE_BIN && (
              <photos-bin projectId={this.projectId} photos={deleted} searchTerm={this.searchTerm} onRestorePhotos={e => this.restorePhotos(e.detail)}></photos-bin>
            )}
          </div>
        )}
        {this.selected.length > 0 && !this.toast && this.canWrite && (
          <div class="footer-selected-info safe-area-bottom">
            <ion-button fill="clear" onClick={() => this.clearSelection()}>
              <ion-icon name="close-outline"></ion-icon>
            </ion-button>
            <div class="center-text">{this.selected.length} photos selected</div>
            {this.album
              ? [
                <ion-button class="ion-hide-md-up" fill="clear" onClick={() => this.onRemovePhotosClick()}>
                  <ion-icon slot="icon-only" name="remove-circle-outline"></ion-icon>
                </ion-button>,
                <ion-button class="ion-hide-md-down link-button" fill="clear" onClick={() => this.onRemovePhotosClick()}>
                  <ion-icon slot="start" name="remove-circle-outline"></ion-icon>
                  <ion-label>remove from {this.album.name}</ion-label>
                </ion-button>,
                <ion-button fill="clear" onClick={() => this.deleteSelectedPhotos()}>
                  <ion-icon slot="icon-only" name="trash"></ion-icon>
                </ion-button>,
              ]
              : [
                <ion-button class="ion-hide-md-up" fill="clear" onClick={() => this.onCreateAlbumClick()}>
                  <ion-icon slot="icon-only" src="/assets/icon/photo-album-icon.svg"></ion-icon>
                </ion-button>,
                <ion-button class="ion-hide-md-down link-button" fill="clear" onClick={() => this.onCreateAlbumClick()}>
                  <ion-icon slot="start" src="/assets/icon/photo-album-icon.svg"></ion-icon>
                  <ion-label>create album</ion-label>
                </ion-button>,
                <ion-button class="ion-hide-md-up" fill="clear" onClick={() => this.assignSelectedPhotosToAlbum()}>
                  <ion-icon slot="icon-only" name="add-circle-outline"></ion-icon>
                </ion-button>,
                <ion-button class="ion-hide-md-down link-button" fill="clear" onClick={() => this.assignSelectedPhotosToAlbum()}>
                  <ion-icon slot="start" name="add-circle-outline"></ion-icon>
                  <ion-label>add to album</ion-label>
                </ion-button>,
                <ion-button class="ion-hide-md-down link-button" fill="clear" onClick={() => this.downloadSelectedPhotos()}>
                  <ion-icon slot="start" name="download-outline"></ion-icon>
                  <ion-label>download</ion-label>
                </ion-button>,
                <ion-button fill="clear" onClick={() => this.deleteSelectedPhotos()}>
                  <ion-icon slot="icon-only" name="trash"></ion-icon>
                </ion-button>,
              ]}
          </div>
        )}
        {this.album && this.selected.length == 0 && this.canWrite && (
          <div class="footer-selected-info ion-padding-horizontal">
            <ion-button id="delete-album" class="link-button" fill="clear" onClick={() => this.deleteAlbum()}>
              <ion-icon slot="icon-only" name="trash"></ion-icon>
              <ion-label>delete {this.album.name}</ion-label>
            </ion-button>
          </div>
        )}
      </Fragment>
    );
  }
}
